const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Returns the first bundle picker / buy box on the page.
 * @returns {HTMLElement | null}
 */
function findBuyBox() {
  return document.querySelector('[data-cs-buybox]') || document.querySelector('product-form-component');
}

/**
 * Scrolls to the buy box and moves focus to the selected bundle option.
 */
function scrollToBuyBox() {
  const target = findBuyBox();
  if (!target) return false;

  target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });

  const focusTarget = target.querySelector('input:checked') || target.querySelector('input, button');
  if (focusTarget instanceof HTMLElement) {
    window.setTimeout(() => focusTarget.focus({ preventScroll: true }), prefersReducedMotion() ? 0 : 450);
  }
  return true;
}

/**
 * Submits the main product form if present, otherwise scrolls to the buy box.
 */
function addToCartOrScroll() {
  const button = document.querySelector(
    'product-form-component [ref="addToCartButton"]:not([disabled])'
  );
  if (button instanceof HTMLButtonElement) {
    button.click();
    return true;
  }
  return scrollToBuyBox();
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const scrollTrigger = event.target.closest('[data-cs-scroll-to-buy]');
  if (scrollTrigger) {
    if (scrollToBuyBox()) event.preventDefault();
    return;
  }

  const addTrigger = event.target.closest('[data-cs-add-to-cart]');
  if (addTrigger) {
    if (addToCartOrScroll()) event.preventDefault();
  }
});

/**
 * Preselects the highlighted bundle (e.g. "2 + 1 offert") when the URL has no explicit variant.
 */
function preselectBundles() {
  if (window.Shopify && window.Shopify.designMode) return;
  const url = new URL(window.location.href);
  if (url.searchParams.has('variant')) return;

  document.querySelectorAll('[data-cs-preselect]').forEach((picker) => {
    if (!(picker instanceof HTMLElement)) return;
    const index = picker.dataset.csPreselect;
    const input = picker.querySelector(`input[data-input-index="${index}"]`);
    if (!(input instanceof HTMLInputElement) || input.checked) return;
    if (input.dataset.optionAvailable === 'false') return;

    customElements.whenDefined('variant-picker').then(() => {
      input.click();
    });
  });
}

class CsSubscription extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-cs-plan-prices]');
    this.prices = json ? JSON.parse(json.textContent || '{}') : {};
    this.planInput = this.querySelector('[data-cs-plan-input]');
    this.planSelect = this.querySelector('[data-cs-plan-select]');
    this.frequency = this.querySelector('[data-cs-frequency]');

    this.addEventListener('change', this.onChange);

    this.section = this.closest('.shopify-section') || document;
    this.section.addEventListener('change', this.onVariantChange);

    // The add-to-cart button is re-rendered (morphed) on variant change: re-apply the plan price.
    const form = document.getElementById(this.dataset.formId || '');
    const buttonsRoot = form?.closest('product-form-component');
    if (buttonsRoot && this.planInput && 'MutationObserver' in window) {
      this.observer = new MutationObserver(() => this.updateCtaPrice());
      this.observer.observe(buttonsRoot, { childList: true, subtree: true, characterData: true });
    }

    this.sync();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.section?.removeEventListener('change', this.onVariantChange);
    this.observer?.disconnect();
  }

  onChange = (event) => {
    event.stopPropagation();
    this.sync();
  };

  onVariantChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    if (this.contains(target)) return;

    const option = target instanceof HTMLSelectElement ? target.selectedOptions[0] : target;
    const variantId = option?.dataset.variantId;
    if (variantId) this.updatePrices(variantId);
  };

  get isSubscription() {
    const checked = this.querySelector('[data-cs-mode]:checked');
    return !checked || (checked instanceof HTMLInputElement && checked.value === 'subscription');
  }

  get currentVariantId() {
    const form = document.getElementById(this.dataset.formId || '');
    const idInput = form?.querySelector('input[name="id"]');
    return idInput instanceof HTMLInputElement ? idInput.value : '';
  }

  sync() {
    if (this.planInput instanceof HTMLInputElement) {
      this.planInput.disabled = !this.isSubscription;
      if (this.planSelect instanceof HTMLSelectElement) {
        this.planInput.value = this.planSelect.value;
      }
    }
    if (this.frequency instanceof HTMLElement) {
      this.frequency.hidden = !this.isSubscription;
    }
    this.updatePrices(this.currentVariantId);
  }

  /** @param {string} variantId */
  updatePrices(variantId) {
    const data = this.prices[variantId];
    if (!data) return;

    const onetime = this.querySelector('[data-cs-onetime-price]');
    if (onetime) onetime.textContent = data.onetime;

    const planId = this.planInput instanceof HTMLInputElement ? this.planInput.value : '';
    const planPrice = this.querySelector('[data-cs-plan-price]');
    if (planPrice && data.plans[planId]) planPrice.textContent = data.plans[planId];
    this.updateCtaPrice();
  }

  /** Shows the subscription price in the rich CTA button while "Abonnement" is selected. */
  updateCtaPrice() {
    const data = this.prices[this.currentVariantId];
    const form = document.getElementById(this.dataset.formId || '');
    const root = form?.closest('product-form-component') || form;
    const ctaPrice = root?.querySelector('[data-cs-cta-price]');
    if (!data || !(ctaPrice instanceof HTMLElement)) return;

    const planId = this.planInput instanceof HTMLInputElement ? this.planInput.value : '';
    const text = this.isSubscription && data.plans[planId] ? data.plans[planId] : data.onetime;
    if (ctaPrice.textContent !== text) ctaPrice.textContent = text;
  }
}

if (!customElements.get('cs-subscription')) {
  customElements.define('cs-subscription', CsSubscription);
}

class CsStickyCta extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('[data-cs-sticky-bar]');
    const buyBox = findBuyBox();
    if (!this.bar || !buyBox || !('IntersectionObserver' in window)) return;

    let buyBoxVisible = true;
    let pastTop = false;

    const update = () => {
      const show = !buyBoxVisible && pastTop;
      this.bar.dataset.visible = String(show);
      this.bar.toggleAttribute('inert', !show);
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        buyBoxVisible = entry.isIntersecting;
        pastTop = entry.boundingClientRect.top < window.innerHeight;
      });
      update();
    });
    this.observer.observe(buyBox);

    this.onScroll = () => {
      pastTop = window.scrollY > window.innerHeight * 0.6;
      update();
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    update();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
  }
}

if (!customElements.get('cs-sticky-cta')) {
  customElements.define('cs-sticky-cta', CsStickyCta);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', preselectBundles);
} else {
  preselectBundles();
}
